import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Shared";
import { api, formatApiError } from "@/lib/api";

export const APP_STATUSES = ["Submitted", "Under Review", "Shortlisted", "Interview", "Selected", "Not Selected"];

export function OpportunityForm({ kind, portalPrefix, onCreated }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        title: "", domain: "", description: "", duration_months: 2, fee_usd: 500,
        mode: "Online", location: "Remote", difficulty: "Intermediate", deadline: "",
        skills: "", eligibility: "", duration: "3 Months",
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/${portalPrefix}/${kind === "project" ? "projects" : "internships"}`, {
                ...form,
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
                duration_months: Number(form.duration_months),
                fee_usd: Number(form.fee_usd),
            });
            toast.success("Submitted for admin approval.");
            setOpen(false);
            setForm({ ...form, title: "", description: "", skills: "" });
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button data-testid={`create-${kind}-button`} size="sm" className="rounded-full" onClick={() => setOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> New {kind === "project" ? "Project" : "Internship"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid={`create-${kind}-dialog`}>
                    <DialogHeader><DialogTitle>Post a {kind === "project" ? "Research Project" : "Internship Program"}</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <Label>Title</Label>
                            <Input data-testid="opp-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-lg" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Domain</Label>
                                <Input data-testid="opp-domain-input" value={form.domain} onChange={(e) => set("domain", e.target.value)} className="rounded-lg" placeholder="AI & ML" />
                            </div>
                            <div className="space-y-2">
                                <Label>Difficulty</Label>
                                <Select value={form.difficulty} onValueChange={(v) => set("difficulty", v)}>
                                    <SelectTrigger data-testid="opp-difficulty-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                                    <SelectContent>{["Beginner", "Intermediate", "Advanced"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea data-testid="opp-description-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-lg" />
                        </div>
                        {kind === "project" ? (
                            <div className="space-y-2">
                                <Label>Duration</Label>
                                <Select value={form.duration} onValueChange={(v) => set("duration", v)}>
                                    <SelectTrigger data-testid="opp-duration-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                                    <SelectContent>{["1 Month", "2 Months", "3 Months", "4 Months", "6 Months"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Duration</Label>
                                        <Select value={String(form.duration_months)} onValueChange={(v) => set("duration_months", v)}>
                                            <SelectTrigger data-testid="opp-duration-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="2">2 months</SelectItem>
                                                <SelectItem value="6">6 months</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Program fee (USD)</Label>
                                        <Input data-testid="opp-fee-input" type="number" value={form.fee_usd} onChange={(e) => set("fee_usd", e.target.value)} className="rounded-lg" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Mode</Label>
                                        <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                                            <SelectTrigger data-testid="opp-mode-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                                            <SelectContent>{["Online", "Offline", "Hybrid"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Location</Label>
                                        <Input data-testid="opp-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} className="rounded-lg" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Application deadline</Label>
                                        <Input data-testid="opp-deadline-input" type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} className="rounded-lg" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Skills (comma separated)</Label>
                                        <Input data-testid="opp-skills-input" value={form.skills} onChange={(e) => set("skills", e.target.value)} className="rounded-lg" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Eligibility</Label>
                                    <Input data-testid="opp-eligibility-input" value={form.eligibility} onChange={(e) => set("eligibility", e.target.value)} className="rounded-lg" />
                                </div>
                            </>
                        )}
                        {kind === "project" && (
                            <div className="space-y-2">
                                <Label>Skills (comma separated)</Label>
                                <Input data-testid="opp-skills-input" value={form.skills} onChange={(e) => set("skills", e.target.value)} className="rounded-lg" />
                            </div>
                        )}
                        <Button data-testid="opp-submit-button" disabled={busy || !form.title || !form.description} className="w-full rounded-full" onClick={submit}>
                            {busy ? "Submitting..." : "Submit for Approval"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export function ApplicantsTable({ applicants, onStatusChange }) {
    const [updating, setUpdating] = useState(null);
    const change = async (appId, status) => {
        setUpdating(appId);
        try {
            await api.put(`/opportunities/applications/${appId}/status`, { status });
            toast.success(`Status updated to ${status}`);
            onStatusChange?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUpdating(null);
        }
    };

    if (applicants.length === 0) return <p className="text-sm text-muted-foreground">No applicants yet.</p>;

    return (
        <div className="card-soft overflow-x-auto" data-testid="applicants-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="p-4">Student</th>
                        <th className="p-4">Opportunity</th>
                        <th className="p-4">Applied</th>
                        <th className="p-4">Payment</th>
                        <th className="p-4">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {applicants.map((a) => (
                        <tr key={a.id} className="border-b border-border/20 last:border-0" data-testid={`applicant-row-${a.id}`}>
                            <td className="p-4"><p className="font-medium">{a.student_name}</p><p className="text-xs text-muted-foreground">{a.student_email}</p></td>
                            <td className="p-4">{a.internship_title}</td>
                            <td className="p-4 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</td>
                            <td className="p-4"><StatusBadge status={a.payment_status} /></td>
                            <td className="p-4">
                                <Select value={a.status} disabled={updating === a.id} onValueChange={(v) => change(a.id, v)}>
                                    <SelectTrigger data-testid={`applicant-status-${a.id}`} className="w-40 rounded-lg"><SelectValue /></SelectTrigger>
                                    <SelectContent>{APP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
